/**
 * CRM Agent GeeVee API
 *
 * Talks to the Claude-powered, persistent agent assistant endpoints
 * (/api/chatbot/agent/geevee/...) — the same LLM the buyer portal uses, not the
 * legacy general flow. `start` resumes the agent's persistent conversation;
 * `sendMessageStream` streams the reply over SSE (fetch, since axios can't read
 * a streaming body).
 */
import api from './index';

const baseURL = (api.defaults.baseURL || 'http://localhost:8000').replace(/\/$/, '');

const getCookie = (name) => {
  if (!document.cookie) return null;
  for (const raw of document.cookie.split(';')) {
    const cookie = raw.trim();
    if (cookie.substring(0, name.length + 1) === `${name}=`) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }
  return null;
};

const streamHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const csrf = getCookie('csrftoken');
  if (csrf) headers['X-CSRFToken'] = csrf;
  // Bearer fallback for iOS/Safari where HttpOnly cookies aren't sent on fetch.
  const token = localStorage.getItem('jwt_access');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

export const geeveeApi = {
  /**
   * Resume (or create) the agent's persistent GeeVee conversation.
   * @param {boolean} forceNew - start a fresh thread ("New chat")
   * @returns {Promise<{id:number, messages:Array}>}
   */
  start: async (forceNew = false) => {
    const res = await api.post('/api/chatbot/agent/geevee/start/', { force_new: forceNew });
    return res.data;
  },

  /**
   * Stream a GeeVee reply over SSE.
   */
  sendMessageStream: async (sessionId, message, onChunk, onComplete, onError) => {
    try {
      const res = await fetch(`${baseURL}/api/chatbot/agent/geevee/message/`, {
        method: 'POST',
        credentials: 'include',
        headers: streamHeaders(),
        body: JSON.stringify({ session_id: sessionId, message }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Failed to send message: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.chunk) {
              onChunk(data.chunk);
            } else if (data.done) {
              onComplete(data);
              return;
            } else if (data.error) {
              onError(new Error(data.error));
              return;
            }
          } catch (e) {
            // ignore partial / non-JSON SSE lines
          }
        }
      }
    } catch (error) {
      onError(error);
    }
  },
};

export default geeveeApi;
